import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store';
import {
    FirebaseNewPortalUser,
    FireBaseNewPortalUsersTable,
    FirebaseUserProtected,
} from 'types/User/firebaseTypes';
import { UserUpdateRoleParams } from 'types/User/updateUserRoleParams';

export interface StoredUser {
    displayName: string | null;
    email: string | null;
    emailVerified: boolean | null;
    photoURL: string | null;
    refreshToken: string | null;
    uid: string | null;
    /** Used to authenticate napi requests as the x-fb-token parameter. Note: fb-tokens expire after 1 hour. */
    idToken: string | null;
    protected: FirebaseUserProtected | null;
    providerIds: string[] | null;
}

export interface AuthenticationDetails {
    user: StoredUser | null;
    supplierId: string | null;
    newPortalUsers: FireBaseNewPortalUsersTable | null;
}

const initialState: AuthenticationDetails = {
    user: null,
    supplierId: null,
    newPortalUsers: null,
};

const getStatus = (user: FirebaseNewPortalUser) => {
    if (!!user.error) return `ERROR: ${user.error}!`;
    if (user.invite_sent && user.invite_opened)
        return 'Invite sent and opened.';
    else if (user.invite_sent) return 'Invite sent.';
    else return 'Invite not sent.';
};

export type MissingUserInformationPayloadType = {
    uid: string;
    roleId: number | undefined;
    lastLogin: number | undefined;
};

export const authenticationSlice = createSlice({
    name: 'authentication',
    initialState,
    reducers: {
        loginSuccessful: (state, action: PayloadAction<StoredUser>) => {
            state.user = action.payload;
            window.gtag('config', 'G-FMGD5ZN8ZW', {
                user_id: state.user.uid,
            });
        },
        retrievedUserProtectedData: (
            state,
            action: PayloadAction<FirebaseUserProtected>
        ) => {
            if (!!state.user) state.user.protected = action.payload;
            const supplierId = action?.payload?.supplierId;
            if (supplierId !== undefined) state.supplierId = supplierId;
        },
        retrievedNewPortalUsers: (
            state,
            action: PayloadAction<FireBaseNewPortalUsersTable>
        ) => {
            const output: FireBaseNewPortalUsersTable = {};
            for (const key in action.payload) {
                const item = action.payload[key];
                item.status = getStatus(item);
                output[key] = item;
            }
            state.newPortalUsers = output;
        },
        updatedUserRole: (
            state,
            _action: PayloadAction<UserUpdateRoleParams>
        ) => {
            return state;
        },
    },
});

export const {
    loginSuccessful,
    retrievedUserProtectedData,
    retrievedNewPortalUsers,
    updatedUserRole,
} = authenticationSlice.actions;
export default authenticationSlice.reducer;

export const useCurrentUserSelector = (state: RootState) =>
    state.authentication;
